import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Image,
  Container,
  Card,
  Text,
  Group,
  Button,
  Divider,
  Title,
  Stack,
  Grid,
  Skeleton,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconCalendar, IconMapPin } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";

const EventPage = () => {
  const [loading, setLoading] = useState(true);
  const [eventDetails, setEventDetails] = useState([]);
  const navigate = useNavigate();

  const ticketForm = useForm({
    tickets: [],
  });

  //Tickets
  useEffect(() => {
    const getTickets = async () => {
      try {
        const response = await axios.get(
          "https://api.dev.fastbreakplatform.com/ticketing/website/tickets"
        );
        ticketForm.setFieldValue(
          "tickets",
          response.data.map((ticket) => ({
            id: ticket.id,
            name: ticket.name,
            description: ticket.description,
            price: ticket.cost.formattedValue,
            count: 0,
          }))
        );
      } catch (error) {
        console.error("Error fetching tickets:", error);
        ticketForm.setFieldValue("tickets", []); //sets the tickets array to empty if an error occurs
      } finally {
        setLoading(false);
      }
    };

    getTickets();
  }, []);

  //Event Details
  useEffect(() => {
    const getEventDetails = async () => {
      try {
        const response = await axios.get(
          "https://api.dev.fastbreakplatform.com/ticketing/website/info"
        );

        setEventDetails(response.data);
      } catch (error) {
        console.error("Error fetching event details:", error);
      } finally {
        setLoading(false);
      }
    };

    getEventDetails();
  }, []);

  const addTicket = (index) => {
    const tickets = ticketForm.getValues().tickets;
    ticketForm.setFieldValue(
      `tickets.${index}.count`,
      tickets[index].count + 1
    );
  };

  const minusTicket = (index) => {
    const tickets = ticketForm.getValues().tickets;
    ticketForm.setFieldValue(
      `tickets.${index}.count`,
      Math.max(0, tickets[index].count - 1)
    );
  };

  const submitTicket = (values) => {
    const ticketAmount = values.tickets.filter((ticket) => ticket.count > 0);

    const ticketData = ticketAmount.map((ticket) => ({
      id: ticket.id,
      name: ticket.name,
      count: ticket.count,
      price: ticket.price,
    }));

    navigate("/checkout-page", {
      state: { tickets: ticketData },
    });
  };

  if (loading) {
    return <Skeleton visible={loading} />;
  }

  return (
    <Container size="lg" style={{ marginTop: 30 }}>
      {/* Banner Image */}
      <Image
        src={
          eventDetails?.websiteBannerURL ??
          "https://www.sportstravelmagazine.com/wp-content/uploads/2024/11/Fastbreak.ai_.jpg"
        }
        radius="md"
        className="banner-image"
      />

      <Grid style={{ marginTop: 30 }}>
        <Grid.Col span={{ base: 12, sm: 8 }} order={{ base: 2, sm: 1 }}>
          <Group align="flex-start">
            <Image
              src={
                eventDetails?.logoURL ??
                "https://pbs.twimg.com/profile_images/1692257149194293248/1SAmbuUS_400x400.jpg"
              }
              radius="md"
              width={100}
              height={100}
            />
            <div>
              <Title order={2}>
                {eventDetails?.name ?? "Event Name Unavailable"}
              </Title>
              <Stack spacing="xs" mt="xs">
                <Group spacing={8}>
                  <IconCalendar size={16} />
                  <Text size="md">
                    {eventDetails?.formattedSchedule ?? "Schedule Unavailable"}
                  </Text>
                </Group>
                <Group spacing={8}>
                  <IconMapPin size={16} />
                  <Text size="md">
                    {eventDetails?.venue?.name ?? "Venue Name Unavailable"}
                  </Text>
                </Group>
              </Stack>
              <Text size="sm" c="dimmed" mt="xs">
                {eventDetails?.venue?.addressLine1 ??
                  "Address Line 1 Unavailable"}
                ,{" "}
                {eventDetails?.venue?.addressLine2 ??
                  "Address Line 2 Unavailable"}
                , {eventDetails?.venue?.city ?? "City Unavailable"},{" "}
                {eventDetails?.venue?.stateOrProvince ??
                  "State/Province Unavailable"}{" "}
                {eventDetails?.venue?.postalCode ?? "Postal Code Unavailable"},{" "}
                {eventDetails?.venue?.country ?? "Country Unavailable"}
              </Text>
            </div>
          </Group>

          {/* Overview Section */}
          <Title order={4} mt="xl">
            Overview
          </Title>
          <Text mt="sm" c="grey">
            {eventDetails?.websiteDescription ??
              "skajdhkjwahdkjshakjdhsakjhdkjsahkdjhsakjdhksjahdkjashkjdsa"}
          </Text>
        </Grid.Col>

        {/*Ticket Selector */}
        <Grid.Col span={{ base: 12, sm: 4 }} order={{ base: 1, sm: 2 }}>
          <form
            onSubmit={ticketForm.onSubmit((values) => submitTicket(values))}
          >
            <Card
              shadow="lg"
              padding="lg"
              className="ticket-card"
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                backgroundColor: "#fff",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
              }}
            >
              <Text weight={500} size="lg" mb="md">
                Event Tickets
              </Text>
              <Divider my="sm" />

              {/* Child Tickets */}
              {ticketForm
                .getValues()
                .tickets?.sort()
                .map((ticket, index) => (
                  <div key={ticket.id}>
                    <Group position="apart" mt="md" noWrap>
                      <div style={{ flex: 1 }}>
                        <Text>{ticket.name ?? "Ticket Name Unavailable"}</Text>
                        <Text size="sm" c="dimmed">
                          {ticket.price ?? "Price Unavailable"} + fees
                        </Text>
                        <Text size="xs" c="dimmed">
                          {ticket.description ?? "Description Unavailable"}
                        </Text>
                      </div>
                      <Group spacing={5} align="center">
                        <Button
                          size="xs"
                          color="gray"
                          variant="outline"
                          onClick={() => minusTicket(index)}
                          radius={15}
                        >
                          -
                        </Button>
                        <Text>{ticket.count ?? 0}</Text>
                        <Button
                          size="xs"
                          color="blue"
                          onClick={() => addTicket(index)}
                          radius={15}
                        >
                          +
                        </Button>
                      </Group>
                    </Group>
                    <Divider my="sm" />
                  </div>
                ))}

              {/* Buy Tickets Button */}
              <Button
                fullWidth
                mt="lg"
                className="buy-button"
                color="green"
                type="submit"
              >
                Buy Tickets
              </Button>
            </Card>
          </form>
        </Grid.Col>
      </Grid>
      <div></div>
      {/* Footer Text */}
      <Text size="xs">
        Powered by <strong>FASTBREAK</strong>
      </Text>
    </Container>
  );
};

export default EventPage;
